import DOMPurify from 'dompurify';

/**
 * 为Markdown内容提供安全的HTML清理配置
 *
 * 支持的功能：
 * - ✅ 基础Markdown元素：标题、段落、列表、表格、引用、代码块等
 * - ✅ 数学公式：完整支持MathML标签和MathJax生成的标签
 * - ✅ 自定义组件标签：支持项目中的自定义提示组件
 *
 * 适用于来自大模型的markdown-it渲染内容，包含markdown-it-mathjax3插件
 *
 * @param html 待清理的HTML内容
 * @param options 自定义DOMPurify配置（可选，为空时使用默认安全配置）
 * @returns 清理后的安全HTML字符串
 */
export function sanitizeMarkdownHtml(html: string, options: any = {}): string {
  // 如果提供了自定义配置，则完全使用自定义配置
  if (Object.keys(options).length > 0) {
    return DOMPurify.sanitize(html, options);
  }

  const defaultOptions: any = {
    ALLOWED_TAGS: [
      // 基础标签
      'p',
      'br',
      'div',
      'span',
      'hr',
      // 标题标签
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      // 文本样式
      'b',
      'strong',
      'i',
      'em',
      'u',
      'mark',
      'small',
      'del',
      'ins',
      'sub',
      'sup',
      // 列表标签
      'ul',
      'ol',
      'li',
      'dl',
      'dt',
      'dd',
      // 表格标签
      'table',
      'thead',
      'tbody',
      'tfoot',
      'tr',
      'th',
      'td',
      'caption',
      'colgroup',
      'col',
      // 引用和代码
      'blockquote',
      'code',
      'pre',
      // 链接和图片
      'a',
      'img',
      // 其他语义标签
      'kbd',
      'abbr',
      'q',
      'cite',
      'dfn',
      'time',
      'var',
      'samp',
      // 数学公式标签 (MathML)
      'math',
      'mrow',
      'mi',
      'mo',
      'mn',
      'msup',
      'msub',
      'mfrac',
      'msqrt',
      'mroot',
      'mstyle',
      'mspace',
      'mtext',
      'mover',
      'munder',
      'munderover',
      'mmultiscripts',
      'mtable',
      'mtr',
      'mtd',
      'merror',
      'mpadded',
      'mphantom',
      'menclose',
      // MathJax 相关标签
      'mjx-container',
      'mjx-math',
      'mjx-semantics',
      'mjx-mrow',
      'mjx-mi',
      'mjx-mn',
      'mjx-mo',
      'mjx-msup',
      'mjx-msub',
      'mjx-mfrac',
      'mjx-msqrt',
      'mjx-mroot',
      'mjx-mtext',
      // 自定义组件标签
      'seek-check-reject-tip',
      'seek-ref-tip',
      'seek-check-replace-tip',
      'markdown-ref-tip',
      'validation-ref-tip',
      'project-entity-tip',
      'seek-project-entity-tip',
      'idea-ref-tip',
      // SVG charts and inline HTML reports from AI (e.g. ```html fences)
      'svg',
      'style',
      'rect',
      'line',
      'text',
      'circle',
      'path',
      'g',
      'defs',
      'title',
      'desc',
    ],
    ADD_ATTR: [
      'org-id',
      'cite-data-id',
      'cite-data-type',
      'data-ref-id',
      'data-doc-id',
      'style',
      'viewBox',
      'fill',
      'stroke',
      'stroke-width',
      'x',
      'y',
      'x1',
      'y1',
      'x2',
      'y2',
      'width',
      'height',
      'text-anchor',
      'font-size',
      'role',
      'aria-label',
      'class',
      'lang',
      'charset',
      'name',
      'content',
      'data-html-embed',
    ],
  };

  return DOMPurify.sanitize(html, defaultOptions);
}

/**
 * Vue directive for safely rendering Markdown HTML content
 * Usage: v-safe-markdown="content"
 */
export const vSafeMarkdown = {
  mounted(el: HTMLElement, binding: { value: string }) {
    el.innerHTML = sanitizeMarkdownHtml(binding.value);
  },
  updated(el: HTMLElement, binding: { value: string }) {
    el.innerHTML = sanitizeMarkdownHtml(binding.value);
  },
};
